import { useEffect, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { useAuth } from "@/contexts/AuthContext";
import { NotificationBell } from "@/components/NotificationBell";
import { Button } from "@/components/ui/button";
import { Users, Dumbbell, ClipboardList, MessageSquare, LogOut, Brain, Settings } from "lucide-react";
import { cn } from "@/lib/utils";

const navItems = [
  { label: "Alunos", path: "/", icon: Users },
  { label: "Exercícios", path: "/exercicios", icon: Dumbbell },
  { label: "Anamneses", path: "/admin/anamneses", icon: ClipboardList },
  { label: "Configurações", path: "/configuracoes", icon: Settings },
];

export default function ProfessorLayout() {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantId, isPlatformOwner, loading: tenantLoading } = useTenant();

  const [tenant, setTenant] = useState<{ logo_url: string | null; brand_color_hsl: string | null; name: string } | null>(null);

  useEffect(() => {
    if (!tenantLoading && isPlatformOwner && !tenantId) {
      navigate("/admin", { replace: true });
    }
  }, [tenantLoading, isPlatformOwner, tenantId, navigate]);

  useEffect(() => {
    if (!tenantId) return;
    supabase
      .from("tenants")
      .select("logo_url, brand_color_hsl, name")
      .eq("id", tenantId)
      .single()
      .then(({ data }) => {
        if (data) setTenant(data as any);
      });
  }, [tenantId]);

  // Inject tenant brand color as CSS variable
  useEffect(() => {
    if (tenant?.brand_color_hsl) {
      document.documentElement.style.setProperty("--primary", tenant.brand_color_hsl);
    }
    return () => {
      document.documentElement.style.removeProperty("--primary");
    };
  }, [tenant?.brand_color_hsl]);

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Branded Header */}
      <header className="border-b border-border/60 bg-card/40 backdrop-blur-md sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Brand area */}
            <button onClick={() => navigate("/")} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              {tenant?.logo_url ? (
                <div className="h-10 w-10 rounded-xl overflow-hidden bg-muted/30 flex items-center justify-center border border-border/40">
                  <img src={tenant.logo_url} alt={tenant.name} className="h-full w-full object-contain" />
                </div>
              ) : (
                <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Brain className="h-5 w-5 text-primary" />
                </div>
              )}
              <div className="hidden sm:block">
                <h1 className="text-lg font-bold text-foreground leading-tight">
                  {tenant?.name || "PersonAI"}
                </h1>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Painel do Professor</p>
              </div>
            </button>

            {/* Nav links (desktop) */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <Button
                  key={item.path}
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate(item.path)}
                  className={cn(
                    "h-9 px-3 text-xs gap-2 transition-colors",
                    isActive(item.path)
                      ? "bg-primary/10 text-primary font-semibold"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </Button>
              ))}
            </nav>

            {/* Actions */}
            <div className="flex items-center gap-1.5">
              <NotificationBell />
              <Button
                variant="ghost"
                size="sm"
                onClick={signOut}
                className="h-8 px-2 text-muted-foreground hover:text-destructive"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile nav */}
        <div className="md:hidden border-t border-border/40 px-4">
          <nav className="flex items-center gap-1 overflow-x-auto py-1.5">
            {navItems.map((item) => (
              <Button
                key={item.path}
                size="sm"
                variant="ghost"
                onClick={() => navigate(item.path)}
                className={cn(
                  "h-8 px-3 text-xs gap-1.5 whitespace-nowrap shrink-0",
                  isActive(item.path)
                    ? "bg-primary/10 text-primary font-semibold"
                    : "text-muted-foreground"
                )}
              >
                <item.icon className="h-3.5 w-3.5" />
                {item.label}
              </Button>
            ))}
          </nav>
        </div>
      </header>

      {/* Page content */}
      <Outlet />
    </div>
  );
}
